"use strict";
/**
 * THE session-authenticated HTTP helper for talking to the proxy server.
 *
 * Every client → server call (both venues + monitor) goes through
 * `apiRequest` here. Venue-specific endpoint functions live in
 * ./kalshi.ts and ./polymarket-us.ts.
 *
 * Browser → proxy server (this repo, src/server) → exchange APIs.
 * SECURITY: authenticates with the session token, never the secret key.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.isNetworkError = exports.NetworkError = exports.ApiRequestError = exports.KALSHI_PRODUCTION_API = exports.KALSHI_DEMO_API = exports.KALSHI_PROXY_URL = void 0;
exports.setApiBaseUrl = setApiBaseUrl;
exports.getApiBaseUrl = getApiBaseUrl;
exports.isSecretKeyError = isSecretKeyError;
exports.apiRequest = apiRequest;
const auth_1 = require("../auth");
const state_1 = require("../state");
const network_error_1 = require("./network-error");
const proxy_url_1 = require("./proxy-url");
// ---------------------------------------------------------------------------
// Base URL configuration
// ---------------------------------------------------------------------------
const getProxyUrl = () => (0, proxy_url_1.proxyUrlFor)(typeof window !== "undefined" ? window.location : undefined);
exports.KALSHI_PROXY_URL = getProxyUrl();
exports.KALSHI_DEMO_API = "https://demo-api.kalshi.co";
exports.KALSHI_PRODUCTION_API = "https://api.elections.kalshi.com/trade-api/v2";
let API_BASE_URL = exports.KALSHI_PROXY_URL;
/** Set the API base URL (proxy, demo, or production). */
function setApiBaseUrl(url) {
    API_BASE_URL = url;
}
/** Get the current API base URL. */
function getApiBaseUrl() {
    return API_BASE_URL;
}
// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------
/** Error thrown for any non-OK proxy/API response. */
class ApiRequestError extends Error {
    constructor(message, statusCode, responseData) {
        super(message);
        this.statusCode = statusCode;
        this.responseData = responseData;
        this.name = "ApiRequestError";
    }
}
exports.ApiRequestError = ApiRequestError;
/**
 * NetworkError means the request never reached the server; ApiRequestError
 * means the server answered and the answer was an error. Re-exported here so
 * callers that already import from this module get both.
 */
var network_error_2 = require("./network-error");
Object.defineProperty(exports, "NetworkError", { enumerable: true, get: function () { return network_error_2.NetworkError; } });
Object.defineProperty(exports, "isNetworkError", { enumerable: true, get: function () { return network_error_2.isNetworkError; } });
/**
 * Check if an error is due to an invalid or missing secret key
 * (401 from the proxy).
 */
function isSecretKeyError(error) {
    if (error instanceof ApiRequestError) {
        return error.statusCode === 401;
    }
    return false;
}
// ---------------------------------------------------------------------------
// Request helper
// ---------------------------------------------------------------------------
/**
 * Make a session-authenticated request to the proxy server.
 *
 * On a 401 INVALID_SESSION response the local session is cleared and the
 * app is routed back to the login page.
 *
 * @param method - HTTP method
 * @param path - Proxy path (e.g. "/kalshi/orders")
 * @param options.params - Query-string parameters
 * @param options.body - JSON request body
 */
async function apiRequest(method, path, options = {}) {
    const sessionToken = (0, auth_1.getSessionToken)();
    if (!sessionToken) {
        throw new Error("Not authenticated. Please login.");
    }
    let url = `${getApiBaseUrl()}${path}`;
    if (options.params && Object.keys(options.params).length > 0) {
        const search = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
            search.set(key, String(value));
        });
        const qs = search.toString();
        if (qs) {
            url += `?${qs}`;
        }
    }
    const fetchOptions = {
        method,
        headers: {
            "Content-Type": "application/json",
            "X-Session-Token": sessionToken,
        },
    };
    if (options.body) {
        fetchOptions.body = JSON.stringify(options.body);
    }
    // try/catch only — no timeout, no retry, no added await. A rejection here
    // means the request never reached the server, which is a different failure
    // from any HTTP status and must not be flattened into one.
    //
    // Deliberately NOT adding a client-side timeout: aborting an in-flight
    // order tells us nothing about whether the exchange received it, and a
    // "failed" order that actually filled is far worse than a slow one. See
    // the PR discussion before revisiting.
    let response;
    try {
        response = await fetch(url, fetchOptions);
    }
    catch (err) {
        // `path`, not `url`: the query string can carry market identifiers and
        // this entry is meant to be copy-pasted out of the device.
        throw (0, network_error_1.networkErrorFrom)(err, path);
    }
    // Non-JSON bodies (nginx error pages, empty responses) must still surface
    // as ApiRequestError carrying the HTTP status — isSecretKeyError() and the
    // re-auth flow key off statusCode, and a bare SyntaxError would lose it.
    let data;
    let parseError = null;
    try {
        data = await response.json();
    }
    catch (err) {
        parseError = err;
    }
    if (!response.ok) {
        if (response.status === 401 && data?.code === "INVALID_SESSION") {
            (0, auth_1.clearSessionToken)();
            (0, state_1.updateState)({
                isAuthenticated: false,
                sessionToken: null,
                currentPage: "login",
                errorMessage: "Session expired. Please login again.",
            });
        }
        throw new ApiRequestError(data?.message ||
            data?.error ||
            `API Error: ${response.status}`, response.status, data);
    }
    // An OK response that isn't JSON is a server contract violation — keep
    // propagating the parse error rather than returning undefined data.
    if (parseError) {
        throw parseError;
    }
    return data;
}
